import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Loader2,
  Pencil,
  RotateCcw,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

/**
 * INTERNAL DEMO — "Rehearse & Rewind" concept.
 * Self-contained prototype with mock data only. No production code, hooks,
 * services, or database are touched. Mirrors the SpotlightDemo convention.
 *
 * Shows the two-part safety net for genie config drift:
 *   1. Rehearsal on save — re-runs the genie's pinned questions and shows which
 *      answers drifted before the change goes live.
 *   2. Rewind — a version timeline with one-click restore.
 */

type ModelKey = "fast" | "smart" | "genius";
type Field = "prompt" | "model" | "knowledge";
type Verdict = "clean" | "regression" | "mixed";

interface QResult {
  q: string;
  baseline: string;
  updated: string;
  status: "held" | "changed";
  tone?: "regression" | "intended" | "surprise";
  note?: string;
}

interface EditPreset {
  id: string;
  label: string;
  genieIcon: GenieIconName;
  field: Field;
  fieldNote: string; // what visibly changes in the config panel
  changeSummary: string; // becomes the version label if kept
  verdict: Verdict;
  results: QResult[];
}

interface GenieVersion {
  id: string;
  label: string;
  summary: string;
  model: ModelKey;
  when: string;
  approved?: boolean;
}

const MODELS: Record<ModelKey, { name: string; blurb: string }> = {
  fast: { name: "Fast", blurb: "Quicker replies" },
  smart: { name: "Smart", blurb: "Balanced — recommended" },
  genius: { name: "Genius", blurb: "Most capable" },
};

const PINNED_QUESTIONS = [
  "Are you open on Saturdays?",
  "Do you offer payment plans?",
  "I've got a really bad toothache — can I be seen today?",
  "How much is a standard check-up and clean?",
  "Where are you located?",
];

const BASELINE: string[] = [
  "Yes — we're open Saturdays 9am to 1pm, and closed Sundays.",
  "We do — interest-free payment plans on any treatment over $300. I can text you the details now.",
  "That sounds painful — we keep emergency slots open every day. I'll book you the next one and flag it as urgent.",
  "A standard check-up and clean is $189, and that includes X-rays if you need them.",
  "We're at 42 Marine Parade, Coolangatta — easy parking right out front.",
];

const held = (i: number): QResult => ({
  q: PINNED_QUESTIONS[i],
  baseline: BASELINE[i],
  updated: BASELINE[i],
  status: "held",
});

const PRESETS: EditPreset[] = [
  {
    id: "model",
    label: "Switch to the Fast model",
    genieIcon: "brain",
    field: "model",
    fieldNote: "Smart → Fast",
    changeSummary: "Switched to the Fast model",
    verdict: "regression",
    results: [
      held(0),
      {
        q: PINNED_QUESTIONS[1],
        baseline: BASELINE[1],
        updated:
          "Yes, we have some payment options — let me get someone to call you back about that.",
        status: "changed",
        tone: "regression",
        note: "Dropped the specific 'interest-free over $300' detail and now defers to a callback.",
      },
      {
        q: PINNED_QUESTIONS[2],
        baseline: BASELINE[2],
        updated: "We have emergency slots. What day works for you?",
        status: "changed",
        tone: "regression",
        note: "Lost the empathy and stopped flagging the booking as urgent.",
      },
      held(3),
      held(4),
    ],
  },
  {
    id: "prompt",
    label: "Tighten the instructions",
    genieIcon: "chat",
    field: "prompt",
    fieldNote: "+1 guard rule added",
    changeSummary: "Tightened the instructions",
    verdict: "clean",
    results: [held(0), held(1), held(2), held(3), held(4)],
  },
  {
    id: "knowledge",
    label: "Update the knowledge base",
    genieIcon: "book-open",
    field: "knowledge",
    fieldNote: "Pricing 2025 → 2026",
    changeSummary: "Updated the knowledge base",
    verdict: "mixed",
    results: [
      {
        q: PINNED_QUESTIONS[0],
        baseline: BASELINE[0],
        updated: "Yes — we've just extended Saturdays to 9am–3pm.",
        status: "changed",
        tone: "intended",
        note: "Reflects the new hours you uploaded. This is the change you meant to make.",
      },
      held(1),
      held(2),
      {
        q: PINNED_QUESTIONS[3],
        baseline: BASELINE[3],
        updated: "A standard check-up and clean is $210.",
        status: "changed",
        tone: "surprise",
        note: "The new pricing doc also bumped this price — a change you might not have expected.",
      },
      held(4),
    ],
  },
];

const INITIAL_VERSIONS: GenieVersion[] = [
  {
    id: "v3",
    label: "v3",
    summary: "Added payment plans to instructions",
    model: "smart",
    when: "2 days ago",
    approved: true,
  },
  { id: "v2", label: "v2", summary: "Updated Saturday hours", model: "smart", when: "5 days ago" },
  { id: "v1", label: "v1", summary: "Initial setup", model: "smart", when: "2 weeks ago" },
];

const BASE_PROMPT =
  "You are the friendly front-desk Genie for Aurora Dental. Greet warmly, answer questions about hours, treatments and pricing, and always offer to book an appointment. Keep replies short and reassuring.";
const ADDED_PROMPT_LINE = "Never quote a price you're unsure of — offer to confirm it by text instead.";

/* ────────────────────────────────────────────────────────────── helpers */

const toneColor = (tone?: QResult["tone"]) => {
  if (tone === "intended") return "#4E9CFF";
  if (tone === "surprise") return "#FFBB33";
  return "#FF4DB8"; // regression
};

/* ────────────────────────────────────────────────────────── config panel */

const SectionCard = ({
  genieIcon,
  title,
  edited,
  children,
}: {
  genieIcon: GenieIconName;
  title: string;
  edited?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={cn(
      "rounded-2xl border bg-white p-5 transition-all",
      edited ? "border-[#FF4DB8]/40 shadow-[0_0_0_3px_rgba(255,77,184,0.08)]" : "border-[#E5EAF0]",
    )}
  >
    <div className="mb-3 flex items-center gap-2.5">
      <GenieIcon name={genieIcon} size={24} className="h-6 w-6" />
      <h3 className="text-sm font-semibold text-[#222B45]">{title}</h3>
      {edited && (
        <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FF4DB8]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#FF4DB8]">
          <Pencil size={10} /> Edited
        </span>
      )}
    </div>
    {children}
  </div>
);

const ConfigPanel = ({
  model,
  promptTightened,
  kbUpdated,
  editedField,
}: {
  model: ModelKey;
  promptTightened: boolean;
  kbUpdated: boolean;
  editedField: Field | null;
}) => (
  <div className="space-y-4">
    <SectionCard genieIcon="chat" title="Personality & instructions" edited={editedField === "prompt"}>
      <p className="text-sm leading-relaxed text-gray-600">{BASE_PROMPT}</p>
      <AnimatePresence>
        {promptTightened && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-2 rounded-lg bg-[#FF4DB8]/5 px-3 py-2 text-sm font-medium leading-relaxed text-[#222B45]"
          >
            {ADDED_PROMPT_LINE}
          </motion.p>
        )}
      </AnimatePresence>
    </SectionCard>

    <SectionCard genieIcon="brain" title="Intelligence" edited={editedField === "model"}>
      <div className="flex flex-wrap gap-2">
        {(Object.keys(MODELS) as ModelKey[]).map((k) => {
          const active = model === k;
          return (
            <div
              key={k}
              className={cn(
                "rounded-xl border px-3 py-2 text-left transition-all",
                active ? "border-transparent text-white shadow-md" : "border-[#E5EAF0] bg-white",
              )}
              style={
                active
                  ? { background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }
                  : undefined
              }
            >
              <div className={cn("text-sm font-semibold", active ? "text-white" : "text-[#222B45]")}>
                {MODELS[k].name}
              </div>
              <div className={cn("text-[11px]", active ? "text-white/80" : "text-gray-500")}>
                {MODELS[k].blurb}
              </div>
            </div>
          );
        })}
      </div>
    </SectionCard>

    <SectionCard genieIcon="book-open" title="Knowledge base" edited={editedField === "knowledge"}>
      <div className="flex flex-wrap gap-2">
        {["Clinic FAQ", "Hours & location", kbUpdated ? "Pricing 2026" : "Pricing 2025"].map((doc) => (
          <span
            key={doc}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium",
              kbUpdated && doc.startsWith("Pricing")
                ? "border-[#FF4DB8]/40 bg-[#FF4DB8]/5 text-[#FF4DB8]"
                : "border-[#E5EAF0] bg-[#F9FAFC] text-gray-600",
            )}
          >
            <GenieIcon name="document" size={14} className="h-3.5 w-3.5" />
            {doc}
          </span>
        ))}
      </div>
    </SectionCard>
  </div>
);

/* ───────────────────────────────────────────────────────── versions panel */

const VersionsPanel = ({
  versions,
  currentId,
  onRestore,
}: {
  versions: GenieVersion[];
  currentId: string;
  onRestore: (v: GenieVersion) => void;
}) => (
  <div className="rounded-2xl border border-[#E5EAF0] bg-white p-5">
    <div className="mb-4 flex items-center gap-2.5">
      <GenieIcon name="sync" size={24} className="h-6 w-6" />
      <h3 className="text-sm font-semibold text-[#222B45]">Version history</h3>
    </div>
    <div className="relative space-y-2.5">
      {versions.map((v) => {
        const isCurrent = v.id === currentId;
        return (
          <div
            key={v.id}
            className={cn(
              "rounded-xl border p-3 transition-all",
              isCurrent ? "border-[#4E9CFF]/40 bg-[#4E9CFF]/5" : "border-[#E5EAF0] bg-white",
            )}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-[#222B45]">{v.label}</span>
                  {isCurrent && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                      Live
                    </span>
                  )}
                  {v.approved && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#4E9CFF]">
                      <GenieIcon name="lock" size={12} className="h-3 w-3" /> Rehearsed
                    </span>
                  )}
                </div>
                <p className="mt-0.5 truncate text-xs text-gray-600">{v.summary}</p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {MODELS[v.model].name} model · {v.when}
                </p>
              </div>
              {!isCurrent && (
                <button
                  onClick={() => onRestore(v)}
                  className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-[#E5EAF0] px-2.5 py-1.5 text-xs font-semibold text-[#4E9CFF] transition-colors hover:bg-[#4E9CFF]/5"
                >
                  <RotateCcw size={13} /> Restore
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────── rehearsal sheet */

const RehearsalResultRow = ({ r, revealed }: { r: QResult; revealed: boolean }) => {
  const [open, setOpen] = useState(false);
  const changed = r.status === "changed";
  const color = changed ? toneColor(r.tone) : "#4E9CFF";

  return (
    <div className="rounded-xl border border-[#E5EAF0] bg-white">
      <button
        disabled={!revealed || !changed}
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-4 py-3 text-left"
      >
        <span
          className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: revealed ? `${color}1A` : "#F1F5F9" }}
        >
          {!revealed ? (
            <Loader2 size={13} className="animate-spin text-gray-400" />
          ) : changed ? (
            <span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
          ) : (
            <Check size={14} style={{ color }} />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-[#222B45]">{r.q}</span>
          <span className="text-xs" style={{ color: revealed ? color : "#94A3B8" }}>
            {!revealed
              ? "Rehearsing…"
              : changed
                ? r.tone === "intended"
                  ? "Changed — intended"
                  : r.tone === "surprise"
                    ? "Changed — unexpected"
                    : "Drifted"
                : "Held steady"}
          </span>
        </span>
        {revealed && changed && (
          <ChevronDown
            size={16}
            className={cn("flex-shrink-0 text-gray-400 transition-transform", open && "rotate-180")}
          />
        )}
      </button>

      <AnimatePresence>
        {open && changed && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden border-t border-[#E5EAF0]"
          >
            <div className="space-y-3 px-4 py-3">
              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-gray-400">
                  Approved answer (v3)
                </div>
                <p className="rounded-lg bg-[#F9FAFC] px-3 py-2 text-sm leading-relaxed text-gray-500 line-through decoration-gray-300">
                  {r.baseline}
                </p>
              </div>
              <div>
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide" style={{ color }}>
                  After your change
                </div>
                <p
                  className="rounded-lg px-3 py-2 text-sm font-medium leading-relaxed text-[#222B45]"
                  style={{ backgroundColor: `${color}0D` }}
                >
                  {r.updated}
                </p>
              </div>
              {r.note && <p className="text-xs leading-relaxed text-gray-500">{r.note}</p>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const RehearsalSheet = ({
  preset,
  resolvedCount,
  done,
  nextVersion,
  onKeep,
  onDiscard,
  onClose,
}: {
  preset: EditPreset;
  resolvedCount: number;
  done: boolean;
  nextVersion: string;
  onKeep: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) => {
  const changedCount = preset.results.filter((r) => r.status === "changed").length;
  const heldCount = preset.results.length - changedCount;

  const summary =
    preset.verdict === "clean"
      ? { color: "#4E9CFF", text: `All ${preset.results.length} answers held steady — safe to publish.` }
      : preset.verdict === "mixed"
        ? { color: "#FFBB33", text: `${heldCount} held · ${changedCount} changed — one you may not have expected.` }
        : { color: "#FF4DB8", text: `${changedCount} of ${preset.results.length} answers drifted — review before publishing.` };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      exit={{ x: "100%" }}
      transition={{ type: "spring", stiffness: 260, damping: 30 }}
      className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col bg-white shadow-2xl"
    >
      {/* header */}
      <div className="flex items-start justify-between gap-3 border-b border-[#E5EAF0] p-6">
        <div className="flex items-center gap-3">
          <div className="p-[2px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
            <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-white">
              <GenieIcon name="clipboard-check" size={24} className="h-6 w-6" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#222B45]">
              {done ? "Rehearsal complete" : "Rehearsing your genie…"}
            </h2>
            <p className="text-xs text-[#64748B]">
              Replaying {preset.results.length} pinned questions · {preset.changeSummary}
            </p>
          </div>
        </div>
        <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600" aria-label="Close">
          <X size={18} />
        </button>
      </div>

      {/* progress / summary */}
      <div className="border-b border-[#E5EAF0] px-6 py-4">
        {!done ? (
          <div className="flex items-center gap-3">
            <Loader2 size={16} className="animate-spin text-[#4E9CFF]" />
            <span className="text-sm font-medium text-[#222B45]">
              Checking answer {Math.min(resolvedCount + 1, preset.results.length)} of {preset.results.length}
            </span>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-3 rounded-xl px-4 py-3"
            style={{ backgroundColor: `${summary.color}12` }}
          >
            <span
              className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: summary.color }}
            >
              {preset.verdict === "clean" ? <Check size={16} /> : "!"}
            </span>
            <span className="text-sm font-semibold text-[#222B45]">{summary.text}</span>
          </motion.div>
        )}
      </div>

      {/* results */}
      <div className="flex-1 space-y-2.5 overflow-y-auto p-6">
        {preset.results.map((r, i) => (
          <RehearsalResultRow key={r.q} r={r} revealed={i < resolvedCount} />
        ))}
      </div>

      {/* footer */}
      {done && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-3 border-t border-[#E5EAF0] p-6"
        >
          <button
            onClick={onDiscard}
            className="rounded-xl border border-[#E5EAF0] px-5 py-3 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50"
          >
            Discard change
          </button>
          <button
            onClick={onKeep}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.02]"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
              boxShadow: "0 10px 24px -8px rgba(78,156,255,0.5)",
            }}
          >
            {preset.verdict === "regression"
              ? `Publish anyway as ${nextVersion}`
              : `Keep & publish ${nextVersion}`}
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

/* ─────────────────────────────────────────────────────────────── toast */

const InlineToast = ({ message }: { message: string | null }) => (
  <AnimatePresence>
    {message && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
        className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-xl bg-[#222B45] px-4 py-2.5 text-sm font-medium text-white shadow-xl"
      >
        {message}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ───────────────────────────────────────────────────────────────── page */

const RehearseDemo = () => {
  const navigate = useNavigate();

  const [versions, setVersions] = useState<GenieVersion[]>(INITIAL_VERSIONS);
  const [currentId, setCurrentId] = useState("v3");
  const [model, setModel] = useState<ModelKey>("smart");
  const [promptTightened, setPromptTightened] = useState(false);
  const [kbUpdated, setKbUpdated] = useState(false);

  const [pendingPreset, setPendingPreset] = useState<EditPreset | null>(null);
  const [rehearsalOpen, setRehearsalOpen] = useState(false);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [done, setDone] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const timers = useRef<number[]>([]);
  const toastTimer = useRef<number | undefined>(undefined);

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => () => clearTimers(), []);

  const flash = (msg: string) => {
    window.clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = window.setTimeout(() => setToast(null), 2600);
  };

  const editedField: Field | null = pendingPreset?.field ?? null;
  const dirty = !!pendingPreset;
  const nextVersionLabel = `v${versions.length + 1}`;

  const applyPreset = (preset: EditPreset) => {
    if (rehearsalOpen) return;
    setPendingPreset(preset);
    if (preset.field === "model") setModel("fast");
    if (preset.field === "prompt") setPromptTightened(true);
    if (preset.field === "knowledge") setKbUpdated(true);
  };

  const resetConfigVisual = () => {
    setModel("smart");
    setPromptTightened(false);
    setKbUpdated(false);
  };

  const startRehearsal = () => {
    if (!pendingPreset) return;
    clearTimers();
    setRehearsalOpen(true);
    setDone(false);
    setResolvedCount(0);
    pendingPreset.results.forEach((_, i) => {
      const t = window.setTimeout(() => setResolvedCount(i + 1), 800 * (i + 1));
      timers.current.push(t);
    });
    const end = window.setTimeout(
      () => setDone(true),
      800 * pendingPreset.results.length + 500,
    );
    timers.current.push(end);
  };

  const closeRehearsal = () => {
    clearTimers();
    setRehearsalOpen(false);
    setDone(false);
    setResolvedCount(0);
  };

  const keepChange = () => {
    if (!pendingPreset) return;
    const newVersion: GenieVersion = {
      id: nextVersionLabel,
      label: nextVersionLabel,
      summary: pendingPreset.changeSummary,
      model: pendingPreset.field === "model" ? "fast" : model,
      when: "just now",
      approved: true,
    };
    setVersions((prev) => [newVersion, ...prev]);
    setCurrentId(newVersion.id);
    setPendingPreset(null);
    closeRehearsal();
    flash(`Published ${newVersion.label} — rehearsed & live`);
  };

  const discardChange = () => {
    setPendingPreset(null);
    resetConfigVisual();
    closeRehearsal();
    flash("Change discarded — genie unchanged");
  };

  const restoreVersion = (v: GenieVersion) => {
    if (dirty) return;
    setCurrentId(v.id);
    setModel(v.model);
    setPromptTightened(false);
    setKbUpdated(false);
    flash(`Rewound to ${v.label} · ${v.summary}`);
  };

  const liveVersion = versions.find((v) => v.id === currentId);

  return (
    <div className="relative min-h-dvh w-full overflow-x-hidden bg-[#F9FAFC]">
      <Helmet>
        <title>Rehearse & Rewind · Internal Demo</title>
      </Helmet>

      {/* top bar */}
      <div className="flex items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full border border-[#E5EAF0] bg-white/70 px-3 py-1.5 text-xs font-medium text-[#64748B] backdrop-blur transition-colors hover:bg-white hover:text-[#222B45]"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-full border border-[#E5EAF0] bg-white px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#64748B] shadow-sm">
          Internal demo · Rehearse &amp; Rewind
        </div>
        <div className="w-[72px]" />
      </div>

      <div className="mx-auto max-w-[1200px] px-4 pb-24 pt-2">
        {/* breadcrumb + header (shows where this lives) */}
        <p className="mb-2 text-xs font-medium text-gray-400">
          My Genies › Aurora Dental — Front Desk Genie › Edit
        </p>
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <GenieIcon name="genie-lamp" size={36} className="h-9 w-9" />
            <div>
              <h1 className="text-2xl font-bold text-[#222B45]">Aurora Dental — Front Desk Genie</h1>
              <p className="text-sm text-[#64748B]">
                Make a change below — your genie rehearses before it goes live.
              </p>
            </div>
          </div>
        </div>

        {/* live-version status banner */}
        <div
          className={cn(
            "mb-6 flex flex-wrap items-center gap-3 rounded-2xl border px-5 py-4",
            dirty ? "border-[#FF4DB8]/30 bg-[#FF4DB8]/5" : "border-[#4E9CFF]/30 bg-[#4E9CFF]/5",
          )}
        >
          <GenieIcon name={dirty ? "clipboard-check" : "lock"} size={24} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[#222B45]">
              {dirty
                ? "Unsaved change — rehearse before it goes live"
                : `Live: ${liveVersion?.label} · matches your last rehearsal`}
            </p>
            <p className="text-xs text-[#64748B]">
              {dirty
                ? "Run the rehearsal to see exactly what this change does to your genie's answers."
                : liveVersion?.summary}
            </p>
          </div>
          {dirty && (
            <button
              onClick={startRehearsal}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.02]"
              style={{
                background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                boxShadow: "0 10px 24px -8px rgba(78,156,255,0.5)",
              }}
            >
              <GenieIcon name="play" size={16} className="h-4 w-4 brightness-0 invert" />
              Save &amp; rehearse
            </button>
          )}
        </div>

        {/* body: config (left) + versions (right) */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_360px]">
          <div>
            {/* demo controls */}
            <div className="mb-4 rounded-2xl border border-dashed border-[#E5EAF0] bg-white px-5 py-4">
              <div className="mb-3 flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-[#FF4DB8]" />
                <span className="text-xs font-semibold text-[#222B45]">Try an edit</span>
                <span className="hidden text-xs text-[#64748B] sm:inline">
                  — pick a change a customer might make, then rehearse it
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {PRESETS.map((p) => {
                  const active = pendingPreset?.id === p.id;
                  return (
                    <button
                      key={p.id}
                      onClick={() => applyPreset(p)}
                      disabled={rehearsalOpen || (dirty && !active)}
                      className={cn(
                        "inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold transition-all disabled:opacity-40",
                        active
                          ? "border-[#FF4DB8]/40 bg-[#FF4DB8]/5 text-[#FF4DB8]"
                          : "border-[#E5EAF0] bg-white text-gray-700 hover:border-[#4E9CFF]/40 hover:bg-[#4E9CFF]/5",
                      )}
                    >
                      <GenieIcon name={p.genieIcon} size={16} className="h-4 w-4" />
                      {p.label}
                    </button>
                  );
                })}
                {dirty && (
                  <button
                    onClick={discardChange}
                    disabled={rehearsalOpen}
                    className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-40"
                  >
                    <X size={13} /> Reset
                  </button>
                )}
              </div>
            </div>

            <ConfigPanel
              model={model}
              promptTightened={promptTightened}
              kbUpdated={kbUpdated}
              editedField={editedField}
            />
          </div>

          <VersionsPanel versions={versions} currentId={currentId} onRestore={restoreVersion} />
        </div>
      </div>

      {/* rehearsal sheet */}
      <AnimatePresence>
        {rehearsalOpen && pendingPreset && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeRehearsal}
              className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
            />
            <RehearsalSheet
              preset={pendingPreset}
              resolvedCount={resolvedCount}
              done={done}
              nextVersion={nextVersionLabel}
              onKeep={keepChange}
              onDiscard={discardChange}
              onClose={closeRehearsal}
            />
          </>
        )}
      </AnimatePresence>

      <InlineToast message={toast} />
    </div>
  );
};

export default RehearseDemo;
